import React from "react";
import * as AuthService from "./auth-service";
import * as TokenStorage from "./token-storage";
import * as ImpersonationStorage from "./impersonation-storage";
import { hardNavigate } from "./hard-navigate";
import type { StoredImpersonation } from "./impersonation-storage";
import { getStoredWorkspaceId, setStoredWorkspaceId, clearStoredWorkspaceId } from "./active-workspace-storage";
import { shouldRefreshToken } from "./jwt";
import { clearMediaObjectUrlCache } from "./media-content";
import { cache as apolloCache } from "./data/apollo-client";
import { omitUndefined, type Omittable } from "@salestrak/ui";
import { beatPresenceOffline } from "./data/presence-provider";
import {
  flushAnalyticsBeforeSignOut,
  resetAnalyticsSession,
} from "#app/lib/analytics/analytics-session";

/**
 * Distinguish network errors (offline, DNS failure, timeout) from actual
 * server-side auth rejection. `fetch` throws a TypeError on network failure,
 * but the message text differs per engine, so match the known variants too.
 */
function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = (err as Error)?.message ?? "";
  return (
    msg.includes("Network request failed") ||
    msg.includes("Failed to fetch") ||
    msg.includes("network") ||
    msg.includes("ECONNREFUSED")
  );
}

/**
 * Drop the stored impersonation and put the superuser back in the workspace
 * they were in before it started. The superuser's own tokens were never
 * touched, so the reload that follows boots them straight back in.
 */
async function leaveImpersonationStorage(session: StoredImpersonation): Promise<void> {
  await ImpersonationStorage.clearStoredImpersonation();
  if (session.returnWorkspaceId) await setStoredWorkspaceId(session.returnWorkspaceId);
  else await clearStoredWorkspaceId();
}

// --- State ---

interface AuthState {
  /** null = still loading from storage, true/false = resolved */
  isAuthenticated: boolean | null;
  accessToken: string | null;
  refreshToken: string | null;
}

type AuthAction =
  | { type: "RESTORE_TOKENS"; accessToken: string; refreshToken: string }
  | { type: "SIGN_IN"; accessToken: string; refreshToken: string }
  | { type: "REFRESH_TOKEN"; accessToken: string }
  | { type: "SIGN_OUT" }
  | { type: "BOOT_COMPLETE" };

const initialState: AuthState = {
  isAuthenticated: null,
  accessToken: null,
  refreshToken: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "RESTORE_TOKENS":
    case "SIGN_IN":
      return {
        ...state,
        isAuthenticated: true,
        accessToken: action.accessToken,
        refreshToken: action.refreshToken,
      };
    case "REFRESH_TOKEN":
      return { ...state, accessToken: action.accessToken };
    case "SIGN_OUT":
      return { ...initialState, isAuthenticated: false };
    case "BOOT_COMPLETE":
      return { ...state, isAuthenticated: false };
    default:
      return state;
  }
}

// --- Context ---

type AuthContextValue = Omittable<{
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (code: string) => Promise<{ created: boolean }>;
  /** Creates the account only — the user signs in via the emailed verification link. */
  signUp: (name: string, email: string, password: string) => Promise<void>;
  /** Consume an emailed verification token; signs the user in on success. */
  verifyEmail: (token: string) => Promise<void>;
  acceptInvite: (
    token: string,
    password: string,
    name?: string
  ) => Promise<{ workspaceId: string }>;
  acceptInviteWithGoogle: (
    token: string,
    code: string
  ) => Promise<{ workspaceId: string; name?: string }>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** The live impersonation session, or null when acting as yourself. */
  impersonation: StoredImpersonation | null;
  /** Superuser-only. Acts as `userId` inside `workspace`, then reloads into it. */
  startImpersonation: (input: {
    userId: string;
    workspace: StoredImpersonation["workspace"];
  }) => Promise<void>;
  /** Ends the session server-side and reloads back into your own account. */
  stopImpersonation: () => Promise<void>;
}>;

const AuthContext = React.createContext<AuthContextValue | null>(null);

// --- Provider ---

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(authReducer, initialState);
  const [impersonation, setImpersonation] = React.useState<StoredImpersonation | null>(null);
  const impersonationRef = React.useRef<StoredImpersonation | null>(null);
  const leavingImpersonationRef = React.useRef(false);

  // Keep tokens in a ref so getAccessToken always has the latest values
  // without needing to re-create the callback on every state change
  const tokensRef = React.useRef({ accessToken: state.accessToken, refreshToken: state.refreshToken });
  // useLayoutEffect ensures the ref is updated before any useEffect in the tree
  // (including child components like AppDataProvider that fetch with these tokens)
  React.useLayoutEffect(() => {
    tokensRef.current = { accessToken: state.accessToken, refreshToken: state.refreshToken };
  }, [state.accessToken, state.refreshToken]);

  // Boot: restore tokens from storage
  React.useEffect(() => {
    (async () => {
      try {
        const stored = await TokenStorage.getStoredTokens();
        const session = await ImpersonationStorage.getStoredImpersonation();
        if (session) {
          // The superuser's own refresh token stays underneath, unused, until
          // the session ends; the access token in play is the session's.
          if (stored && !ImpersonationStorage.isImpersonationExpired(session)) {
            impersonationRef.current = session;
            setImpersonation(session);
            dispatch({
              type: "RESTORE_TOKENS",
              accessToken: session.accessToken,
              refreshToken: stored.refreshToken,
            });
            return;
          }
          await leaveImpersonationStorage(session);
        }
        if (stored) {
          if (shouldRefreshToken(stored.accessToken)) {
            try {
              const { tokens } = await AuthService.refreshTokens(
                stored.refreshToken
              );
              if (tokens?.accessToken) {
                const refreshToken =
                  tokens.refreshToken ?? stored.refreshToken;
                await TokenStorage.setStoredTokens({
                  accessToken: tokens.accessToken,
                  refreshToken,
                });
                dispatch({
                  type: "RESTORE_TOKENS",
                  accessToken: tokens.accessToken,
                  refreshToken,
                });
                return;
              }
            } catch (err) {
              if (isNetworkError(err)) {
                // Offline — keep tokens, let the user in with cached data.
                // getAccessToken() will retry the refresh when connectivity returns.
                dispatch({
                  type: "RESTORE_TOKENS",
                  accessToken: stored.accessToken,
                  refreshToken: stored.refreshToken,
                });
                return;
              }
              // Actual auth rejection (token revoked, server 401/403) — sign out
              await TokenStorage.clearStoredTokens();
              dispatch({ type: "BOOT_COMPLETE" });
              return;
            }
          }

          dispatch({
            type: "RESTORE_TOKENS",
            accessToken: stored.accessToken,
            refreshToken: stored.refreshToken,
          });
          return;
        }
      } catch {
        // ignore storage errors
      }
      dispatch({ type: "BOOT_COMPLETE" });
    })();
  }, []);

  // Validate, persist, and enter the signed-in state. The ref is seeded
  // synchronously so an authenticated call fired the moment a flow resolves
  // (the invite flows fire the acceptInvitation mutation immediately) picks
  // up the JWT we just received instead of racing the useLayoutEffect that
  // normally syncs the ref — losing that race surfaces as UNAUTHENTICATED.
  const completeSignIn = React.useCallback(
    async (tokens: AuthService.AuthTokens | undefined, invalidMessage: string) => {
      if (!tokens?.accessToken) {
        throw new Error(invalidMessage);
      }
      // A session already in this tab means the identity is CHANGING, not
      // starting — the emailed verification and invite links both land in a
      // tab that may already be signed in as someone else. The media and
      // Apollo caches are module-level and shared by every client the app
      // builds, and cache-and-network serves synchronously, so without this
      // the previous account's entities paint into the new one's first frame
      // (the same leak signOut resets for). Tokens are swapped first so the
      // refetches the reset triggers carry the new bearer, and the dispatch
      // that re-renders comes last so nothing paints in between.
      const replacingSession = !!tokensRef.current.accessToken;
      await TokenStorage.setStoredTokens(tokens);
      tokensRef.current = {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      };
      if (replacingSession) {
        clearMediaObjectUrlCache();
        await apolloCache.reset();
      }
      dispatch({
        type: "SIGN_IN",
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
    },
    []
  );

  const signIn = React.useCallback(
    async (email: string, password: string) => {
      const { tokens } = await AuthService.login(email, password);
      await completeSignIn(tokens, "Invalid login response");
    },
    [completeSignIn]
  );

  const signInWithGoogle = React.useCallback(
    async (code: string) => {
      const { tokens, created } = await AuthService.loginWithGoogle(code);
      await completeSignIn(tokens, "Invalid login response");
      return { created: !!created };
    },
    [completeSignIn]
  );

  // No auto-login: the account starts email-unverified and the server
  // rejects password login until the emailed link is consumed.
  const signUp = React.useCallback(
    async (name: string, email: string, password: string) => {
      await AuthService.createAccount({ name, email, password });
    },
    []
  );

  // Verification only flips the server-side verified flag; it never
  // establishes a session (a link opened by anyone but the owner would
  // otherwise be a login-CSRF). The screen routes to sign-in on success.
  const verifyEmail = React.useCallback(async (token: string) => {
    await AuthService.verifyEmail(token);
  }, []);

  const acceptInvite = React.useCallback(
    async (token: string, password: string, name?: string) => {
      const { tokens, workspaceId } = await AuthService.acceptInvite(
        token,
        password,
        name
      );
      await completeSignIn(tokens, "Invalid invitation response");
      return { workspaceId };
    },
    [completeSignIn]
  );

  const acceptInviteWithGoogle = React.useCallback(
    async (token: string, code: string) => {
      const { tokens, workspaceId, name } =
        await AuthService.acceptInviteWithGoogle(token, code);
      await completeSignIn(tokens, "Invalid invitation response");
      return omitUndefined({ workspaceId, name });
    },
    [completeSignIn]
  );

  const stopImpersonation = React.useCallback(async () => {
    const session = impersonationRef.current;
    if (!session || leavingImpersonationRef.current) return;
    leavingImpersonationRef.current = true;
    if (!ImpersonationStorage.isImpersonationExpired(session)) {
      await AuthService.endImpersonation(session.accessToken).catch(() => {});
    }
    await leaveImpersonationStorage(session);
    hardNavigate("/");
  }, []);

  React.useEffect(() => {
    if (!impersonation) return;
    const remaining = new Date(impersonation.expiresAt).valueOf() - Date.now();
    const timer = setTimeout(() => void stopImpersonation(), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [impersonation, stopImpersonation]);

  const signOut = React.useCallback(async () => {
    // Signing out of an impersonation (including the Apollo error link's
    // UNAUTHENTICATED path once the session ends) returns to the superuser's
    // own account instead of ending it.
    if (impersonationRef.current) {
      await stopImpersonation();
      return;
    }
    // Drop the presence indicator through the server's OFFLINE fast path
    // (which deletes the presence key outright) so teammates stop seeing this
    // member as online within a round trip instead of up to the 90s TTL.
    // MUST run before clearStoredTokens — the beat needs a live token, and
    // PresenceProvider only unmounts once SIGN_OUT has dispatched. Bounded and
    // never-throwing, so a slow or failing beat can't stall sign-out; on the
    // Apollo `onUnauthenticated` path the beat just fails against the dead
    // token (the error link's own signing-out latch stops it re-entering here).
    await beatPresenceOffline();
    // Same constraint for analytics: events queued since the last timed flush
    // (this sign-out's own `auth.sign_out`) need the live token to be sent.
    // Bounded, like the presence beat.
    await flushAnalyticsBeforeSignOut();
    await TokenStorage.clearStoredTokens();
    // Drop permission-gated photo blobs cached under this identity — no page
    // reload happens here, so another user signing in to this tab would
    // otherwise reuse them straight from the module-level cache.
    clearMediaObjectUrlCache();
    // Same reasoning for the Apollo cache: it is ONE module-level object
    // shared by every client the app builds, and cache-and-network serves it
    // synchronously — without this reset, this user's entities paint into the
    // next user's first frame in the same tab. `discardWatches` stops the
    // mounted queries from fetching again with no token; they unmount on
    // SIGN_OUT, and a skipped query watches again when it next runs.
    await apolloCache.reset({ discardWatches: true });
    // Whatever did not send is dropped, not held for the next user.
    resetAnalyticsSession();
    dispatch({ type: "SIGN_OUT" });
  }, [stopImpersonation]);

  const getAccessToken = React.useCallback(async (): Promise<string | null> => {
    if (leavingImpersonationRef.current) return null;
    const session = impersonationRef.current;
    if (session) {
      if (!ImpersonationStorage.isImpersonationExpired(session)) return session.accessToken;
      void stopImpersonation();
      return null;
    }
    const { accessToken, refreshToken } = tokensRef.current;
    if (!accessToken || !refreshToken) return null;

    if (!shouldRefreshToken(accessToken)) {
      return accessToken;
    }

    try {
      const { tokens } = await AuthService.refreshTokens(refreshToken);
      if (tokens?.accessToken) {
        const newRefresh = tokens.refreshToken ?? refreshToken;
        await TokenStorage.setStoredTokens({
          accessToken: tokens.accessToken,
          refreshToken: newRefresh,
        });
        dispatch({ type: "REFRESH_TOKEN", accessToken: tokens.accessToken });
        return tokens.accessToken;
      }
    } catch (err) {
      if (isNetworkError(err)) {
        // Network is down — return the current (possibly expired) token
        // rather than signing the user out. The request that triggered this
        // refresh will hit the same network error and Apollo will report
        // it; when connectivity returns, the next request retries the
        // refresh. Better than tearing down their session on a flaky Wi-Fi.
        return accessToken;
      }
      // Server rejected the refresh token — sign out
      await TokenStorage.clearStoredTokens();
      clearMediaObjectUrlCache();
      dispatch({ type: "SIGN_OUT" });
    }

    return null;
  }, [stopImpersonation]);

  const startImpersonation = React.useCallback(
    async (input: { userId: string; workspace: StoredImpersonation["workspace"] }) => {
      if (impersonationRef.current) throw new Error("Already impersonating");
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const res = await AuthService.startImpersonation(token, input.userId, input.workspace.id);
      await ImpersonationStorage.setStoredImpersonation({
        accessToken: res.accessToken,
        expiresAt: res.expiresAt,
        user: res.user,
        workspace: input.workspace,
        returnWorkspaceId: await getStoredWorkspaceId(),
      });
      await setStoredWorkspaceId(input.workspace.id);
      // A full reload rather than an in-place swap: the workspace boot, the
      // websocket and every module-level cache key on the signed-in identity,
      // and a fresh page is the one teardown that misses none of them.
      hardNavigate(`/${input.workspace.urlPath}`);
    },
    [getAccessToken]
  );

  const value = React.useMemo(
    () => ({
      state,
      signIn,
      signInWithGoogle,
      signUp,
      verifyEmail,
      acceptInvite,
      acceptInviteWithGoogle,
      signOut,
      getAccessToken,
      impersonation,
      startImpersonation,
      stopImpersonation,
    }),
    [
      state,
      signIn,
      signInWithGoogle,
      signUp,
      verifyEmail,
      acceptInvite,
      acceptInviteWithGoogle,
      signOut,
      getAccessToken,
      impersonation,
      startImpersonation,
      stopImpersonation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --- Hook ---

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
